import { useMemo } from 'react';
import { timeAgo } from '@modules/common/helpers/date';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import { StatusChip } from '@common/components/StatusChip';

import { TopButtons } from './TopButtons';

type Props = {
  id: string;
  versionNr: number;
  updatedAt: Date;
  latest: boolean;
  current: boolean;
};

export const VersionCard = ({ id, versionNr, updatedAt, latest, current }: Props) => {
  const { t } = useTranslation('interface');
  const updatedText = useMemo(() => timeAgo(updatedAt), [updatedAt]);

  return (
    <Stack
      sx={{
        alignItems: 'flex-start',
        border: 'none',
        borderColor: 'unset',
        borderBottom: '1px solid',
        borderBottomColor: 'neutral.lighter',
        padding: '8px 16px 16px',
        gap: 1,
      }}
      component='article'
    >
      <Stack
        sx={{
          alignItems: 'center',
          flexDirection: 'row',
          gap: 1,
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        {latest || current ? (
          <Stack
            sx={{
              flexBasis: '100%',
              alignItems: 'center',
              flexDirection: 'row',
              height: '100%',
            }}
            gap={1}
          >
            {latest && (
              <StatusChip status={t('versioning.version_card.latest')} color='success.main' />
            )}
            {current && (
              <StatusChip status={t('versioning.version_card.current')} color='info.main' />
            )}
          </Stack>
        ) : null}
      </Stack>
      <Stack sx={{ alignItems: 'center', flexDirection: 'row', gap: 2, width: '100%' }}>
        <Typography
          align='left'
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            flexGrow: 1,
          }}
        >
          {t('versioning.version_card.version', { version: versionNr })}
        </Typography>
        {!current && <TopButtons id={id} />}
      </Stack>
      <Typography variant='caption' fontSize={10}>
        <Box component='span' color='neutral.darker'>
          {t('versioning.version_card.last_updated', { date: updatedText })}
        </Box>{' '}
      </Typography>
    </Stack>
  );
};
